<script lang="ts">
  import { onMount } from "svelte"
  import { slide } from "svelte/transition"
  import { xhr, toast } from "$app/utils"
  import Button from "$app/components/ButtonSpinner.svelte"
  import EditGoalStatus from "./EditGoalStatus.svelte"
  import { Newspaper, TriangleAlert, ChevronRight, Lock } from "lucide-svelte"
  import _ from "lodash"
  import Tiptap from "$app/components/Tiptap.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import { parentTypes } from "./methods"
  import { onlyTextButtons } from "$app/components/Tiptap/buttons"
  import { appStore } from "$app/store.svelte"

  interface Props {
    goal: GoalType
    disableParent?: boolean
    disableType?: boolean
    oncreate?: (goal: GoalType) => void
  }

  let {
    goal,
    disableParent = false,
    disableType = false,
    oncreate
  }: Props = $props()

  let parentGoals: GoalType[] = $state([])
  let end_of_quarter = appStore.end_of_quarter
  let end_of_year = appStore.end_of_year

  let errors: any = $state({})

  let deptItems = appStore.departments.map((d) => {
    return { value: d.id, label: d.name }
  })
  let root_goal_id: number = null

  let annualGoals: GoalType[] = []
  let assigneeValue = { value: 0, label: "" }

  const goalsWithOptions = ["AnnualGoal", "QuarterlyGoal"]

  const currentDate = new Date()

  const initialForm = {
    name: goal?.name || "",
    type: goal?.type || "",
    expected_at: null,
    description: goal?.description || "",
    leadership_notes: goal?.leadership_notes || "",
    obstacles_text: goal?.obstacles_text || "",
    assignee_type: goal?.assignee_type || "Department",
    assignee_id: goal?.assignee_id || 0,
    status: goal?.status || "on_track",
    visibility: goal?.visibility || "plan",
    parent_goal_id: goal?.parent_goal_id,
    parent_goal_type: goal?.parent_goal_type
  }

  let form = $state({ ...initialForm } as GoalType)
  let isLoading = false

  let show_description = $state(false)
  let description = $state("")
  let show_leadership_notes = $state(false)
  let leadership_notes = $state("")
  let show_obstacles = $state(false)
  let obstacles_text = $state("")

  async function initializeForm(): Promise<void> {
    isLoading = true

    let year = new Date().getFullYear()
    const ey = end_of_year.toJSON()
    if (end_of_year !== null && ey.length > 4) {
      year = +ey.slice(0, 4)
    }

    try {
      const resp = await xhr.get(
        `/plans/${appStore.plan.id}/goals/new.json?parent_goal_id=${goal?.parent_goal_id}&type=${goal?.type}`
      )
      parentGoals = _.sortBy(resp.data.goals as GoalType[], "assignee_position")
      end_of_quarter = resp.data.expected_dates.end_of_quarter
      end_of_year = resp.data.expected_dates.end_of_year
      root_goal_id = resp.data.root_goal_id
      updateType()
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      console.error(e)
      toast("There was an error loading the goal form", "error")
    } finally {
      isLoading = false
    }
  }

  // Save New Goal
  let savingGoal = $state(false)

  async function createGoal(): Promise<void> {
    savingGoal = true

    errors = {}

    try {
      let data = {}
      const parentGoal = parentGoals.find((g) => g.id === form.parent_goal_id)
      if (parentGoal) {
        data = { goal: { ...form, parent_goal_type: parentGoal.type } }
      } else {
        data = { goal: { ...form, parent_goal_type: null } }
      }
      const resp = await xhr.post<GoalType>(
        `/plans/${appStore.plan.id}/goals.json`,
        data
      )
      appStore.goals = [...appStore.goals, resp.data]
      toast("The goal has been created.", "success")
      oncreate(resp.data)
    } catch (e: any) {
      toast("There was an error saving the new goal", "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        Honeybadger.notify(e)
      }
    } finally {
      savingGoal = false
    }
  }

  function updateType(): void {
    if (form.type === "AnnualGoal") {
      form.expected_at = end_of_year
      form.parent_goal_type = "LongTermGoal"
      assigneeValue = { value: 0, label: "" }
    } else if (form.type === "QuarterlyGoal") {
      form.expected_at = end_of_quarter
      form.parent_goal_type = "AnnualGoal"
    } else if (form.type === "LongTermGoal") {
      form.parent_goal_id = root_goal_id
      form.parent_goal_type = "RootGoal"
      form.visibility = "plan"
    }
    document.getElementById("goal_name")?.focus()
  }

  function updateParentGoal(): void {
    if (form.type === "QuarterlyGoal") {
      const parentGoal = annualGoals.find((g) => g.id === form.parent_goal_id)
      if (form.assignee_id === null) {
        form.assignee_id = parentGoal?.assignee_id
        const dept = deptItems.find((d) => d.value === form.assignee_id)
        assigneeValue = dept
      }
    }
  }

  // Setting correctly the expected at is tricky and more depends on the parent_goal and goal type
  async function updateExpectedAt(): Promise<void> {
    if (form.expected_at >= end_of_year) {
      try {
        const resp = await xhr.get(
          `/plans/${appStore.plan.id}/goals/new.json?expected_at=${form.expected_at}&parent_goal_id=${form.parent_goal_id}&type=${form.type}`
        )
        parentGoals = _.sortBy(
          resp.data.goals as GoalType[],
          "assignee_position"
        )
        end_of_quarter = resp.data.expected_dates.end_of_quarter
        end_of_year = resp.data.expected_dates.end_of_year
      } catch (e) {
        Honeybadger.notify(e)
        toast("There was an error loading updated annual goals", "error")
      }
    }
  }

  export function changesCount(): number {
    let count = 0
    Object.keys(initialForm).forEach((key) => {
      if (initialForm[key] !== form[key]) {
        count++
      }
    })

    return count
  }

  onMount(async () => {
    initializeForm()
  })
</script>

<div class="mt-3 sm:mt-5 overflow-visible w-full">
  <div class="flex w-full items-center">
    <h3 class="text-2xl leading-6 font-medium text-gray-900">
      Create New {disableParent ? "Sub Goal" : "Goal"}
    </h3>
  </div>
  <div style="width:90vw"></div>
  <div class="mt-6">
    <div class="grid grid-cols-6 gap-y-3 gap-x-4">
      <div class="col-span-6">
        <p class="mb-1">Name</p>
        <div class="input-cont" class:error={errors.name}>
          <input
            id="goal_name"
            type="text"
            bind:value={form.name}
            class="input"
          />
          <div class="msg">{errors.name}</div>
        </div>
      </div>
      <div
        class="col-span-6 sm:col-span-3 lg:col-span-2"
        class:error={errors.type}
      >
        <label for="goal_type" class="label">Goal Type</label>
        <div class="input-cont" class:error={errors.type}>
          <select
            id="goal_type"
            bind:value={form.type}
            onchange={() => updateType()}
            disabled={disableType}
          >
            <option value="">Select Goal Type</option>
            <option value="LongTermGoal">Long-term Goal</option>
            <option value="AnnualGoal">Annual Goal</option>
            <option value="QuarterlyGoal">Quarterly Goal</option>
          </select>
          <div class="msg">{errors.type}</div>
        </div>
      </div>

      <div
        class="col-span-6 sm:col-span-3 lg:col-span-2"
        class:opacity-60={!goalsWithOptions.includes(form.type)}
      >
        <label for="parent_goal_id" class="label">Parent Goal</label>
        <div
          class="input-cont"
          class:error={errors.parent_goal &&
            goalsWithOptions.includes(form.type)}
        >
          <select
            id="parent_goal_id"
            bind:value={form.parent_goal_id}
            onchange={() => updateParentGoal()}
            disabled={!goalsWithOptions.includes(form.type) || disableParent}
          >
            {#if parentGoals.length}
              {@const goalsForm = parentGoals.filter(
                (g) => parentTypes[form.type] === g.type
              )}
              {#each goalsForm as pg (pg.id)}
                <option value={pg.id}>{pg.name}</option>
              {:else}
                <option value="">No Parent Goals for current year</option>
              {/each}
            {:else}
              <option value="">No Parent Goals for current year</option>
            {/if}
          </select>
          <div class="msg">{errors.parent_goal}</div>
        </div>
      </div>

      {#if appStore.current_user.role === "leader" || appStore.current_user.role === "admin"}
        <div
          class="col-span-6 sm:col-span-3 lg:col-span-2"
          class:opacity-60={!goalsWithOptions.includes(form.type)}
          class:error={errors.type}
        >
          <label for="visibility" class="label">Goal Visibility</label>
          <div class="input-cont" class:error={errors.visibility}>
            <select
              id="goal_visibility"
              bind:value={form.visibility}
              disabled={!goalsWithOptions.includes(form.type)}
            >
              <option value="plan">All Members</option>
              <option value="leadership">Only Leadership Team</option>
            </select>
            <div class="msg">{errors.visibility}</div>
          </div>
        </div>
      {/if}

      {#if goalsWithOptions.includes(form.type)}
        <div
          class="col-span-6 sm:col-span-3 lg:col-span-2"
          style="z-index: 210;"
        >
          <label for="status" class="label">Status</label>
          <div class="w-full">
            <EditGoalStatus
              id="goal_status"
              type="Goal"
              goalOrTask={form}
              update={false}
              editable={true}
              onupdate={(t) => (form.status = t.status)}
            />
          </div>
        </div>
        <div
          class="col-span-6 sm:col-span-3 lg:col-span-2 relative"
          style="z-index: 200;"
        >
          <label for="expected_at" class="label">Expected By</label>
          <div class="input-cont" class:error={errors.expected_at}>
            <input
              type="date"
              class="input"
              bind:value={form.expected_at}
              onchange={updateExpectedAt}
            />
            <div class="msg">{errors.expected_at}</div>
          </div>
        </div>

        <div
          class="col-span-6 sm:col-span-3 lg:col-span-2"
          class:error={errors.assignee_id}
        >
          <label for="assignee_id" class="label">Assignee</label>
          <div
            class="mt-1 input-cont assignee-cont"
            class:error={errors.assignee}
          >
            <select id="goal_assignee_id" bind:value={form.assignee_id}>
              {#if appStore.departments.length}
                {#each appStore.departments as dept (dept.id)}
                  <option value={dept.id}>{dept.name}</option>
                {/each}
              {:else}
                <option value="">No Departments</option>
              {/if}
            </select>
            <div class="msg">{errors.assignee}</div>
          </div>
        </div>
      {/if}

      <div class="col-span-6">
        <div class="flex items-center mb-1 mt-2">
          <button
            id="toggle-description"
            class={[
              "flex items-center rotate-icon",
              show_description ? "open" : ""
            ]}
            onclick={() => (show_description = !show_description)}
            type="button"
          >
            <ChevronRight
              class="text-gray-500 mr-2 size-5 transition-transform rotate-icon-90"
            />
            <Newspaper class="text-gray-400 mr-2 h-4 w-4" />
            <div class="text-sm text-gray-700">Description / Action Plan</div>
          </button>
        </div>
        {#if show_description}
          <div transition:slide class="pl-5 pt-1">
            <Tiptap
              id="goal_description"
              buttons={onlyTextButtons}
              content={description}
              placeholder="Enter a description of what is going to be accomplished"
              onupdate={({ html }) => (form.description = html)}
            />
          </div>
        {/if}
      </div>

      {#if appStore.current_user.role === "leader" || appStore.current_user.role === "admin"}
        <div class="col-span-6">
          <div class="flex items-center mb-1">
            <button
              id="toggle-leadership-notes"
              class={[
                "flex items-center rotate-icon",
                show_leadership_notes ? "open" : ""
              ]}
              onclick={() => (show_leadership_notes = !show_leadership_notes)}
            >
              <ChevronRight class="text-gray-500 mr-2 size-5 rotate-icon-90" />

              <Lock class="text-gray-400 mr-2 h-4 w-4" />
              <div class="text-sm text-gray-700">Leadership Team Notes</div>
            </button>
          </div>
          {#if show_leadership_notes}
            <div transition:slide class="pl-5 pt-1">
              <Tiptap
                id="goal_leadership_notes"
                buttons={onlyTextButtons}
                content={leadership_notes}
                placeholder="Enter any private notes here which are visible only for Leadership Team members."
                onupdate={({ html }) => (form.leadership_notes = html)}
              />
            </div>
          {/if}
        </div>
      {/if}

      <div class="col-span-6">
        <div class="flex items-center mb-1">
          <button
            id="toggle-obstacles"
            class={["flex items-center", show_obstacles ? "open" : ""]}
            onclick={() => (show_obstacles = !show_obstacles)}
          >
            <ChevronRight class="text-gray-500 mr-2 h-5 w-5 rotate-icon-90" />

            <TriangleAlert class="text-gray-400 mr-2 h-4 w-4" />
            <div class="text-sm text-gray-700">Obstacles</div>
          </button>
        </div>
        {#if show_obstacles}
          <div transition:slide class="pl-5 pt-1">
            <Tiptap
              id="goal_obstacles_text"
              placeholder="Enter any obstacles to accomplishing this goal"
              buttons={onlyTextButtons}
              content={obstacles_text}
              onupdate={({ html }) => (form.obstacles_text = html)}
            />
          </div>
        {/if}
      </div>
      <div class="col-span-6 py-3 text-right mt-2">
        <Button
          class="btn btn-rounded success btn-big"
          processing={savingGoal}
          onclick={() => {
            createGoal()
          }}
        >
          Create Goal
        </Button>
      </div>
    </div>
  </div>
</div>
